import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "./Sheet";
import { fmtAestDate, addDaysAestISO } from "../lib/format";
import { TASK_TYPES } from "../lib/taskTypes";
import TimePicker from "./TimePicker";

/**
 * Modal to complete a task: capture outcome + optionally schedule a follow-up.
 *
 * Props:
 *  task    - the task being completed
 *  users   - [{id, name}] for the assignee picker
 *  onDone  - called after a successful complete; receives { followup } so callers can refresh
 */
export default function CompleteTaskModal({ task, users = [], onClose, onDone }) {
  const toast = useToast();
  const [outcome, setOutcome] = useState("");
  const [createFollowup, setCreateFollowup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Default follow-up: same assignee + contact + summary prefix, due in 7 days (AEST)
  const [fu, setFu] = useState(() => {
    return {
      summary: `Follow up: ${task?.summary || ""}`.slice(0, 200),
      due_date: addDaysAestISO(7),
      due_time: "",
      assigned_to_id: task?.assigned_to_id || "",
      task_type: task?.task_type || "Phone Call",
    };
  });

  async function save() {
    setErr(""); setSaving(true);
    try {
      if (!outcome.trim()) throw new Error("Outcome is required");
      const payload = { outcome: outcome.trim() };
      if (createFollowup) {
        if (!fu.summary.trim()) throw new Error("Follow-up summary is required");
        if (!fu.due_date) throw new Error("Follow-up due date is required");
        payload.followup = {
          summary: fu.summary.trim(),
          due_date: fu.due_date,
          due_time: fu.due_time || null,
          assigned_to_id: fu.assigned_to_id || null,
          contact_id: task?.contact_id || null,
          task_type: fu.task_type || "Phone Call",
        };
      }
      const { data } = await api.post(`/tasks/${task.id}/complete`, payload);
      toast.success(createFollowup ? "Task completed · follow-up created" : "Task completed");
      onDone?.(data);
      onClose();
    } catch(e) {
      const m = e?.response ? toastErrorMessage(e) : (e.message || "Something went wrong");
      setErr(m); toast.error(m);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onClose={onClose} title="Complete task"
      footer={
        <div className="flex gap-2 justify-end">
          <button className="cesco-btn-ghost" onClick={onClose} data-testid="complete-cancel">Cancel</button>
          <button className="cesco-btn-primary" onClick={save} disabled={saving}
                  data-testid="complete-save">
            {saving ? "Saving…" : (createFollowup ? "Complete + create follow-up" : "Mark complete")}
          </button>
        </div>}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Task</div>
          <div className="font-semibold">{task?.summary}</div>
          <div className="text-xs text-gray-500 mt-1">
            {task?.company_name ? `${task.company_name} · ` : ""}Due {fmtAestDate(task?.due_date)}
            {task?.contact_name ? <> · 👤 {task.contact_name}</> : null}
          </div>
        </div>

        <div>
          <label className="cesco-label">Outcome *</label>
          <textarea className="cesco-input" rows={4}
                    placeholder="What happened? e.g. Spoke to Paul - PO approved, shipping Friday."
                    value={outcome} onChange={e=>setOutcome(e.target.value)}
                    data-testid="complete-outcome"/>
          <div className="flex flex-wrap gap-1 mt-2">
            {[
              "Call completed - positive",
              "Call completed - no decision",
              "Left voicemail",
              "Email sent",
              "Meeting held",
              "Quote issued",
              "No response",
            ].map(preset => (
              <button key={preset} type="button" onClick={()=>setOutcome(preset)}
                className="pill cursor-pointer hover:bg-gray-100"
                style={{border:"1px dashed #B6BAC5", color:"#555", background:"#fff"}}
                data-testid={`chip-${preset}`}>
                {preset}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none text-sm pt-2 border-t">
          <input type="checkbox" checked={createFollowup}
                 onChange={e=>setCreateFollowup(e.target.checked)}
                 data-testid="complete-followup-toggle"/>
          <span className="font-medium">Create a follow-up task</span>
        </label>

        {createFollowup && (
          <div className="space-y-3 bg-[var(--theme-accent-50)] rounded-lg p-3" data-testid="followup-fields">
            <div>
              <label className="cesco-label">Type *</label>
              <select className="cesco-input" value={fu.task_type}
                      onChange={e=>setFu({...fu, task_type:e.target.value})}
                      data-testid="followup-type">
                {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="cesco-label">Summary *</label>
              <textarea className="cesco-input" rows={2} value={fu.summary}
                        onChange={e=>setFu({...fu, summary:e.target.value})}
                        data-testid="followup-summary"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="cesco-label">Due date *</label>
                <input className="cesco-input" type="date" value={fu.due_date}
                       onChange={e=>setFu({...fu, due_date:e.target.value})}
                       data-testid="followup-due"/>
              </div>
              <div>
                <label className="cesco-label">Due time</label>
                <TimePicker value={fu.due_time}
                            onChange={v => setFu({...fu, due_time: v})}
                            testId="followup-time"/>
              </div>
            </div>
            {users.length > 0 && (
              <div>
                <label className="cesco-label">Assign to</label>
                <select className="cesco-input" value={fu.assigned_to_id}
                        onChange={e=>setFu({...fu, assigned_to_id:e.target.value})}>
                  <option value="">(me)</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
            {task?.contact_name && (
              <div className="text-xs text-gray-600">
                Will be linked to <b>{task.contact_name}</b> (same as original task).
              </div>
            )}
          </div>
        )}

        {err && <div className="text-sm text-red-600" data-testid="complete-error">{err}</div>}
      </div>
    </Sheet>
  );
}
